import React, { useEffect, useRef, useState } from 'react';
import { configData } from "../config";

// Live's Agent & Athlete Search box (the text box of its custom blocks 128 and 129), which works as
// jQuery UI's autocomplete (1.8) does there: from two letters on it lists the agents and athletes
// whose names match, and choosing one opens their page. The arrow keys move through the list and
// leave the typed text as it is; a search that finds nobody lists "No Results", which does nothing
// when chosen. Live lists the names alphabetically; this site's search (user/getagentathletelist)
// does not sort them.

const MIN_LENGTH = 2;
const DELAY = 300;
const NO_RESULTS = 'No Results';

// The names found for each search while the page is open, as live keeps them
const cache = {};

const findNames = async (term) => {
    if (!cache[term]) {
        const response = await fetch(configData.APP_URL + 'user/getagentathletelist?query=' + encodeURIComponent(term));
        const data = await response.json();
        cache[term] = (data?.data?.result || [])
            .filter((item) => item.label && item.link)
            .sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
    }
    return cache[term];
};

const AgentAthleteSearch = ({ id }) => {
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(false);
    // The list under the box ({ items, top, left, width }), or null when it is closed
    const [menu, setMenu] = useState(null);
    const [active, setActive] = useState(-1);
    const inputRef = useRef(null);
    const timer = useRef(null);
    // The term last searched for: the answer to an earlier search is dropped
    const latest = useRef('');

    useEffect(() => () => clearTimeout(timer.current), []);

    const close = () => {
        setMenu(null);
        setActive(-1);
    };

    const search = (term) => {
        if (term.length < MIN_LENGTH) {
            latest.current = '';
            setLoading(false);
            close();
            return;
        }
        latest.current = term;
        setLoading(true);
        findNames(term).then((found) => {
            if (latest.current !== term) return;
            // Under the box, as jQuery UI places it; 6px narrower, since jQuery UI takes the list's
            // padding and border off the box's width and live's theme then counts them inside it
            const input = inputRef.current;
            setMenu({
                items: found.length ? found : [{ label: NO_RESULTS }],
                top: input.offsetTop + input.offsetHeight,
                left: input.offsetLeft,
                width: input.offsetWidth - 6,
            });
            setActive(-1);
        }).catch(() => {
            if (latest.current === term) close();
        }).finally(() => {
            if (latest.current === term) setLoading(false);
        });
    };

    const choose = (item) => {
        close();
        if (item.link) window.location.href = item.link;
    };

    const onChange = (event) => {
        const term = event.target.value;
        setValue(term);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => search(term), DELAY);
    };

    const onKeyDown = (event) => {
        const count = menu ? menu.items.length : 0;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!menu) {
                search(value);
            } else if (event.key === 'ArrowDown') {
                setActive((index) => (index === count - 1 ? -1 : index + 1));
            } else {
                setActive((index) => (index === -1 ? count - 1 : index - 1));
            }
        } else if (event.key === 'Enter' && menu && active >= 0) {
            event.preventDefault();
            choose(menu.items[active]);
        } else if (event.key === 'Escape' && menu) {
            close();
        }
    };

    const onBlur = () => {
        clearTimeout(timer.current);
        latest.current = '';
        setLoading(false);
        close();
    };

    return (
        <div className="agent-athlete-search">
            <p>
                <input ref={inputRef} id={id} type="text" value={value} style={{ width: '100%' }}
                    className={'agent-athlete-search__input' + (loading ? ' agent-athlete-search__input--loading' : '')}
                    autoComplete="off" aria-autocomplete="list" aria-haspopup="true"
                    onChange={onChange} onKeyDown={onKeyDown} onBlur={onBlur} />
            </p>
            {menu && (
                // mousedown would take the focus from the box, which closes the list
                <ul className="agent-athlete-search__menu" role="listbox" style={{ top: menu.top, left: menu.left, width: menu.width }}
                    onMouseDown={(event) => event.preventDefault()} onMouseLeave={() => setActive(-1)}>
                    {menu.items.map((item, index) => (
                        <li key={item.link || item.label} className="agent-athlete-search__item" role="option" aria-selected={index === active}>
                            <div className={'agent-athlete-search__name' + (index === active ? ' agent-athlete-search__name--active' : '')}
                                onMouseEnter={() => setActive(index)} onClick={() => choose(item)}>{item.label}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AgentAthleteSearch;
